import { useState, lazy, Suspense } from "react";
import { useT } from "@/i18n/LanguageContext";

import { BrandLogos } from "@/components/BrandLogos";
import paLogo from "@/assets/pa-logo.png";

// Lazy load BriefFormDialog - only needed when user clicks CTA
const BriefFormDialog = lazy(() => import("@/components/BriefFormDialog").then(m => ({ default: m.BriefFormDialog })));

export const HeroSection = () => {
  const t = useT();
  const [briefOpen, setBriefOpen] = useState(false);

  return (
    <>
      <section className="relative w-full h-screen overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 w-full h-full">
          <video 
            autoPlay 
            loop 
            muted 
            playsInline 
            preload="auto"
            poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1920' height='1080'%3E%3Crect fill='%23000000' width='100%25' height='100%25'/%3E%3C/svg%3E"
            className="w-full h-full object-cover"
            onCanPlay={(e) => e.currentTarget.play().catch(() => {})}
            onError={(e) => e.currentTarget.style.display = 'none'}
          >
            <source src="/videos/hero-v2.webm" type="video/webm" />
            <source src="/videos/hero-v2.mp4" type="video/mp4" />
          </video>
          {/* Light wash overlay for readability on the light theme */}
          <div className="absolute inset-0" style={{
            background: "linear-gradient(180deg, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.55) 45%, rgba(0,0,0,0.75) 100%)"
          }} />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center justify-start h-full text-center px-4 md:px-6 pt-[8vh] md:pt-[10vh]">
          {/* Logo */}
          <div className="mb-6 md:mb-7">
            <img src={paLogo} alt="PA Logo" width={180} height={180} fetchPriority="high" decoding="sync" loading="eager" className="w-[26vw] md:w-[14vw] max-w-[180px] min-w-[110px]" />
          </div>

          {/* Text Block - vertical flow */}
          <div className="max-w-4xl">
            {/* Main Heading */}
            <h1 style={{
              fontSize: "clamp(26px, 3.6vw, 52px)",
              lineHeight: "1.15",
              letterSpacing: "0.015em"
            }} className="text-white mb-5 md:mb-6 text-4xl font-bold">
              {t("НОВЫЙ СТАНДАРТ ВИЗУАЛЬНОГО МАРКЕТИНГА")}
            </h1>
            
            {/* Slogan - breathing pulse effect */}
            <p style={{
              fontSize: "clamp(13px, 1.3vw, 17px)",
              letterSpacing: "0.03em",
              backgroundImage: "linear-gradient(180deg, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0.95) 60%, rgba(255,255,255,0.45) 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent"
            }} className="lowercase pa-slogan text-sm font-light mb-7 md:mb-8">
              {t("делаем то, что не могут другие")}
            </p>
            
            {/* CTA Button - compact wine accent */}
            <div>
              <button
                onClick={() => setBriefOpen(true)}
                aria-label={t("НАЧАТЬ ВМЕСТЕ С НАМИ - сотрудничество с PROPAGANDA AI")}
                className="inline-flex items-center rounded-full bg-wine px-4 py-2 md:px-5 md:py-2.5 text-[9.5px] md:text-[11px] font-medium uppercase tracking-[0.14em] text-white shadow-[0_12px_28px_-16px_hsl(var(--wine))] transition-all duration-300 hover:brightness-110 active:scale-95"
              >
                {t("НАЧАТЬ ВМЕСТЕ С НАМИ")}
              </button>

            </div>

          </div>
        </div>

        {/* Brand Logos - inside hero at bottom */}
        <div className="absolute bottom-[60px] md:bottom-[80px] left-0 right-0 z-30">
          <BrandLogos />
        </div>

        {/* Smooth fade transition to white */}
        <div className="absolute bottom-0 left-0 right-0 h-[250px] pointer-events-none z-20" style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 100%)'
        }} />
      </section>
      
      {briefOpen && (
        <Suspense fallback={null}>
          <BriefFormDialog open={briefOpen} onOpenChange={setBriefOpen} />
        </Suspense>
      )}
    </>
  );
};
